import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Result,
  Row,
  Select,
  Space,
  Steps,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useAuthStore } from '@/store/authStore';
import workshopService from '@/services/workshop.service';
import employeeService from '@/services/employee.service';
import {
  HOLD_LABELS,
  HOLD_TAG_COLOR,
  holdLabel,
  qcLabel,
  RESPONSIBILITY_LABELS,
  STAGE_LABELS,
  STAGE_TAG_COLOR,
  stageLabel,
} from './workshopLabels';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

function hasPerm(user: { is_super_admin?: boolean; permissions?: string[] } | null | undefined, code: string) {
  return Boolean(user?.is_super_admin || user?.permissions?.includes(code) || user?.permissions?.includes('asset.maintenance.edit'));
}

const WorkshopJobWorkspacePage: React.FC = () => {
  const { caseId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canView = hasPerm(user, 'workshop.view') || hasPerm(user, 'asset.maintenance.view') || user?.permissions?.includes('asset.view');
  const canExecute = hasPerm(user, 'workshop.execute');
  const canHold = hasPerm(user, 'workshop.hold.manage');
  const canLabour = hasPerm(user, 'workshop.labour.record');
  const canAssign = hasPerm(user, 'workshop.assign');
  const canQc = hasPerm(user, 'workshop.qc');
  const canRelease = hasPerm(user, 'workshop.release');

  const [intakeOpen, setIntakeOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [labourOpen, setLabourOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [hireExceptionRecorded, setHireExceptionRecorded] = useState(false);
  const [linkMrOpen, setLinkMrOpen] = useState(false);
  const [linkMrId, setLinkMrId] = useState('');
  const [intakeForm] = Form.useForm();
  const [holdForm] = Form.useForm();
  const [labourForm] = Form.useForm();
  const [releaseForm] = Form.useForm();

  const jobQ = useQuery({
    queryKey: ['workshop', 'job', caseId],
    queryFn: () => workshopService.getJob(caseId),
    enabled: Boolean(canView && caseId),
  });

  const partsQ = useQuery({
    queryKey: ['workshop', 'parts', caseId],
    queryFn: () => workshopService.listParts(caseId),
    enabled: Boolean(canView && caseId),
  });

  const employeesQ = useQuery({
    queryKey: ['hr', 'employees', 'workshop'],
    queryFn: () => employeeService.list(),
    enabled: canAssign || canLabour,
  });

  const facilitiesQ = useQuery({
    queryKey: ['workshop', 'facilities'],
    queryFn: () => workshopService.listFacilities() as Promise<{
      profiles?: Array<{ id: string; company_location_id: string }>;
      workshop_locations?: Array<{ id: string; location_name: string }>;
    }>,
    enabled: canExecute,
  });

  const pmvId = jobQ.data?.pmv_profile?.id;
  const responsibilityQ = useQuery({
    queryKey: ['workshop', 'responsibility', pmvId],
    queryFn: () => workshopService.getResponsibility(pmvId!),
    enabled: Boolean(pmvId),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['workshop', 'job', caseId] });
    void qc.invalidateQueries({ queryKey: ['workshop', 'jobs'] });
    void qc.invalidateQueries({ queryKey: ['workshop', 'parts', caseId] });
  };

  const openExec = useMutation({
    mutationFn: () => {
      const profile = facilitiesQ.data?.profiles?.[0];
      const loc = facilitiesQ.data?.workshop_locations?.[0];
      return workshopService.openExecution({
        maintenance_case_id: caseId,
        routing_mode: 'FACILITY',
        company_location_id: profile?.company_location_id ?? loc?.id ?? null,
        facility_profile_id: profile?.id ?? null,
      });
    },
    onSuccess: () => {
      message.success('Workshop job opened');
      invalidate();
    },
    onError: (e: any) => {
      const code = e?.response?.data?.errors?.code || e?.response?.data?.code;
      if (code === 'VENDOR_RESPONSIBLE' || String(e?.message || '').includes('VENDOR')) {
        message.error('Vendor maintenance responsibility — internal repair is blocked without an approved exception.');
      } else if (code === 'SHARED_RESPONSIBILITY') {
        message.error('Shared maintenance responsibility — approval / exception required before contractor repair.');
      } else {
        message.error(e?.response?.data?.message || e?.message || 'Could not open workshop execution');
      }
    },
  });

  const run = (fn: () => Promise<unknown>, ok: string) =>
    fn()
      .then(() => {
        message.success(ok);
        invalidate();
      })
      .catch((e: any) => message.error(e?.response?.data?.message || e?.message || 'Action failed'));

  const c = jobQ.data?.case;
  const ex = jobQ.data?.active_execution;
  const assetLabel =
    c?.asset?.asset_code ||
    c?.asset?.asset_name ||
    (typeof c?.asset_id === 'string' ? c.asset_id.slice(0, 8) : 'Equipment');

  const stepIndex = useMemo(() => {
    const order = ['QUEUED', 'RECEIVED', 'DIAGNOSING', 'PLANNED', 'ASSIGNED', 'IN_REPAIR', 'QC_PENDING', 'READY_FOR_RELEASE', 'RELEASED'];
    const i = order.indexOf(ex?.execution_stage || '');
    return i < 0 ? 0 : i;
  }, [ex?.execution_stage]);

  if (!canView) {
    return <Result status="403" title="Permission required" />;
  }

  if (jobQ.isError) {
    return (
      <Result
        status="error"
        title="Unable to load workshop job"
        subTitle={(jobQ.error as Error)?.message}
        extra={
          <Button onClick={() => jobQ.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (!jobQ.isLoading && !c) {
    return <Result status="404" title="Maintenance case not found" />;
  }

  const responsibility = responsibilityQ.data;
  const hireBlocked =
    responsibility?.code === 'VENDOR_RESPONSIBLE' ||
    responsibility?.code === 'SHARED_RESPONSIBILITY' ||
    c?.cost_responsibility === 'VENDOR' ||
    c?.cost_responsibility === 'SHARED';
  const sharedBlocked = responsibility?.code === 'SHARED_RESPONSIBILITY' || c?.cost_responsibility === 'SHARED';
  const vendorBlocked =
    responsibility?.code === 'VENDOR_RESPONSIBLE' || c?.cost_responsibility === 'VENDOR';

  return (
    <WorkspaceLayout>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/asset/workshop')}
        style={{ paddingLeft: 0, marginBottom: 8 }}
      >
        Workshop Operations
      </Button>

      <EnterprisePageHeader
        documentLabel="Workshop job"
        documentNumber={c?.case_number || '…'}
        statusTag={
          <Space wrap size={4}>
            <span>{assetLabel}</span>
            {ex?.execution_stage && (
              <Tag color={STAGE_TAG_COLOR[ex.execution_stage]}>{stageLabel(ex.execution_stage)}</Tag>
            )}
            {ex?.hold_reason && <Tag color={HOLD_TAG_COLOR}>{holdLabel(ex.hold_reason)}</Tag>}
          </Space>
        }
        subtitle={c?.title}
        primaryActions={
          <Space wrap>
            {ex?.hold_reason === 'WAITING_PARTS' && (
              <Button
                type="primary"
                onClick={() =>
                  navigate(
                    `/scm/inventory/material-requests/new?maintenanceCaseId=${encodeURIComponent(caseId)}&returnTo=${encodeURIComponent(`/asset/workshop/jobs/${caseId}`)}`
                  )
                }
              >
                Request parts (MR)
              </Button>
            )}
            {!ex && canExecute && (
              <Button
                type="primary"
                loading={openExec.isPending}
                disabled={hireBlocked && !hireExceptionRecorded}
                onClick={() => openExec.mutate()}
              >
                Open workshop intake
              </Button>
            )}
            {ex && canExecute && ex.execution_stage === 'QUEUED' && (
              <Button type="primary" onClick={() => setIntakeOpen(true)}>
                Record intake
              </Button>
            )}
            {ex && canExecute && ex.execution_stage === 'IN_REPAIR' && !ex.hold_reason && (
              <Button
                type="primary"
                onClick={() =>
                  run(
                    () => workshopService.markWorkComplete(ex.id, { expected_version: ex.version }),
                    'Marked work complete — QC required'
                  )
                }
              >
                Work complete
              </Button>
            )}
            {ex && canQc && ex.execution_stage === 'QC_PENDING' && (
              <>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() =>
                    run(
                      () => workshopService.recordQc(ex.id, { result: 'PASS', expected_version: ex.version }),
                      'QC passed'
                    )
                  }
                >
                  QC pass
                </Button>
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={() =>
                    run(
                      () => workshopService.recordQc(ex.id, { result: 'FAIL', expected_version: ex.version }),
                      'QC failed — returned to repair'
                    )
                  }
                >
                  QC fail
                </Button>
              </>
            )}
            {ex && canRelease && ex.execution_stage === 'READY_FOR_RELEASE' && (
              <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={() => setReleaseOpen(true)}>
                Authorize release
              </Button>
            )}
          </Space>
        }
      />

      {vendorBlocked && !ex && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Maintenance responsibility: Vendor"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              This equipment is on an active hire agreement where the vendor is responsible for maintenance.
              Normal internal garage repair is blocked. Record a hire exception only for approved emergency
              contractor work — commercial hire outcomes stay with Hire Management.
            </Paragraph>
          }
          action={
            canRelease && responsibility?.agreement_id ? (
              <Button
                size="small"
                onClick={() => {
                  Modal.confirm({
                    title: 'Record hire exception',
                    content: 'Allow contractor-funded repair for this case? This does not change hire commercial status.',
                    onOk: () =>
                      workshopService
                        .createHireException(caseId, {
                          hire_agreement_id: responsibility.agreement_id!,
                          reason: 'Emergency contractor repair approved',
                        })
                        .then(() => {
                          message.success('Exception recorded');
                          setHireExceptionRecorded(true);
                          invalidate();
                        }),
                  });
                }}
              >
                Record exception
              </Button>
            ) : undefined
          }
        />
      )}

      {sharedBlocked && !ex && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Maintenance responsibility: Shared — approval required"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              This hire agreement splits maintenance responsibility. Contractor garage repair is blocked until
              an approved Workshop hire exception is recorded. Commercial hire outcomes stay with Hire Management.
            </Paragraph>
          }
          action={
            canRelease && responsibility?.agreement_id ? (
              <Button
                size="small"
                onClick={() => {
                  Modal.confirm({
                    title: 'Record hire exception',
                    content:
                      'Record approved shared-responsibility exception for this case? Hire commercial status is unchanged.',
                    onOk: () =>
                      workshopService
                        .createHireException(caseId, {
                          hire_agreement_id: responsibility.agreement_id!,
                          reason: 'Shared responsibility exception approved for contractor repair',
                        })
                        .then(() => {
                          message.success('Exception recorded');
                          setHireExceptionRecorded(true);
                          invalidate();
                        }),
                  });
                }}
              >
                Record exception
              </Button>
            ) : undefined
          }
        />
      )}

      {ex?.hold_reason && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`On hold: ${holdLabel(ex.hold_reason)}`}
          description={
            ex.hold_set_at
              ? `Hold since ${dayjs(ex.hold_set_at).format('DD MMM YYYY HH:mm')}`
              : 'Work is blocked until this hold is cleared.'
          }
          action={
            canHold ? (
              <Button
                size="small"
                onClick={() =>
                  run(() => workshopService.clearHold(ex.id), 'Hold cleared')
                }
              >
                Clear hold
              </Button>
            ) : undefined
          }
        />
      )}

      {ex && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Steps
            size="small"
            current={stepIndex}
            items={[
              { title: 'Queued' },
              { title: 'Received' },
              { title: 'Diagnose' },
              { title: 'Plan' },
              { title: 'Assign' },
              { title: 'Repair' },
              { title: 'QC' },
              { title: 'Ready' },
              { title: 'Released' },
            ]}
            responsive
          />
        </Card>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={16}>
          <Card size="small" title="Job header">
            <Descriptions
              size="small"
              column={{ xs: 1, sm: 1, md: 2, lg: 3 }}
              styles={{ content: { wordBreak: 'break-word' }, label: { whiteSpace: 'nowrap' } }}
            >
              <Descriptions.Item label="Work order">{c?.case_number}</Descriptions.Item>
              <Descriptions.Item label="Case status">{c?.status}</Descriptions.Item>
              <Descriptions.Item label="Priority">{c?.priority}</Descriptions.Item>
              <Descriptions.Item label="Workshop stage">
                {ex ? stageLabel(ex.execution_stage) : 'Not opened'}
              </Descriptions.Item>
              <Descriptions.Item label="QC">{ex ? qcLabel(ex.qc_state) : '—'}</Descriptions.Item>
              <Descriptions.Item label="Received">
                {ex?.intake_at ? dayjs(ex.intake_at).format('DD MMM YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="PMV status">
                {jobQ.data?.pmv_profile?.operational_status || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Responsibility">
                {responsibility
                  ? RESPONSIBILITY_LABELS[responsibility.code] || responsibility.code
                  : c?.cost_responsibility
                    ? RESPONSIBILITY_LABELS[c.cost_responsibility] || c.cost_responsibility
                    : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Maintenance case">
                <Link to={`/asset/maintenance/cases/${caseId}`}>Open case form</Link>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" title="Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              {ex && canHold && (
                <Button block icon={<PauseCircleOutlined />} onClick={() => setHoldOpen(true)}>
                  Apply hold
                </Button>
              )}
              {ex && canLabour && (
                <Button block icon={<PlayCircleOutlined />} onClick={() => setLabourOpen(true)}>
                  Start labour
                </Button>
              )}
              {ex && canExecute && (
                <Select
                  style={{ width: '100%' }}
                  placeholder="Advance stage…"
                  options={Object.entries(STAGE_LABELS).map(([value, label]) => ({ value, label }))}
                  onSelect={(to_stage) =>
                    run(
                      () =>
                        workshopService.changeStage(ex.id, {
                          to_stage,
                          expected_version: ex.version,
                        }),
                      'Stage updated'
                    )
                  }
                />
              )}
              {ex && canRelease && ex.execution_stage === 'QC_PENDING' && (
                <Button
                  block
                  onClick={() => {
                    Modal.confirm({
                      title: 'Waive QC?',
                      content: 'Only when policy allows for low-risk service work.',
                      onOk: () =>
                        workshopService
                          .waiveQc(ex.id, {
                            reason: 'Supervisor waiver for eligible service',
                            expected_version: ex.version,
                          })
                          .then(() => {
                            message.success('QC waived');
                            invalidate();
                          }),
                    });
                  }}
                >
                  Waive QC
                </Button>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <Paragraph>{c?.description || 'No description on the maintenance case.'}</Paragraph>
              ),
            },
            {
              key: 'intake',
              label: 'Intake',
              children: ex ? (
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Intake time">
                    {ex.intake_at ? dayjs(ex.intake_at).format('DD MMM YYYY HH:mm') : 'Not recorded'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Complaint / notes">{ex.intake_notes || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Condition">{ex.intake_condition_notes || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Diagnosis">{ex.diagnosis_notes || '—'}</Descriptions.Item>
                </Descriptions>
              ) : (
                <Empty description="Open workshop intake to capture meter and condition." />
              ),
            },
            {
              key: 'parts',
              label: 'Parts',
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Parts use Inventory"
                    description="Create or link material requests against this maintenance case. Workshop does not keep a separate stock ledger. Unknown cost is shown as unavailable, never zero."
                  />
                  {ex?.hold_reason === 'WAITING_PARTS' && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Waiting for parts"
                      description="Create a material request for this job (Inventory soft-reserve + issue path), then clear the hold when parts arrive."
                      action={
                        <Button
                          size="small"
                          type="primary"
                          onClick={() =>
                            navigate(
                              `/scm/inventory/material-requests/new?maintenanceCaseId=${encodeURIComponent(caseId)}&returnTo=${encodeURIComponent(`/asset/workshop/jobs/${caseId}`)}`
                            )
                          }
                        >
                          Create MR
                        </Button>
                      }
                    />
                  )}
                  <Space wrap style={{ marginBottom: 12 }}>
                    <Button
                      type="primary"
                      onClick={() =>
                        navigate(
                          `/scm/inventory/material-requests/new?maintenanceCaseId=${encodeURIComponent(caseId)}&returnTo=${encodeURIComponent(`/asset/workshop/jobs/${caseId}`)}`
                        )
                      }
                    >
                      Request parts for this job
                    </Button>
                    <Button
                      onClick={() =>
                        navigate('/scm/inventory/material-requests')
                      }
                    >
                      Open Inventory material requests
                    </Button>
                    {canExecute && (
                      <Button
                        onClick={() => {
                          setLinkMrId('');
                          setLinkMrOpen(true);
                        }}
                      >
                        Link existing request
                      </Button>
                    )}
                  </Space>
                  <Typography.Title level={5} style={{ marginTop: 8 }}>
                    Material requests
                  </Typography.Title>
                  <Table
                    size="small"
                    rowKey={(r: any) => r.id || r.request_number}
                    loading={partsQ.isLoading}
                    dataSource={(partsQ.data as any)?.requests || []}
                    locale={{
                      emptyText:
                        'No material requests linked to this case yet. Use “Request parts for this job” or link an existing MR.',
                    }}
                    columns={[
                      { title: 'Request', dataIndex: 'request_number', key: 'n' },
                      { title: 'Status', dataIndex: 'request_status', key: 's' },
                      {
                        title: 'Est. cost',
                        dataIndex: 'total_estimated_cost',
                        key: 'c',
                        render: (v: number | null) =>
                          v == null ? <Text type="secondary">Unavailable</Text> : Number(v).toFixed(2),
                      },
                      {
                        title: '',
                        key: 'open',
                        render: (_: unknown, row: { id?: string }) =>
                          row.id ? (
                            <Link to={`/scm/inventory/material-requests/${row.id}`}>Open</Link>
                          ) : null,
                      },
                    ]}
                  />
                  <Typography.Title level={5} style={{ marginTop: 16 }}>
                    Material issuances
                  </Typography.Title>
                  <Table
                    size="small"
                    rowKey={(r: any) => r.id || r.issuance_number}
                    loading={partsQ.isLoading}
                    dataSource={(partsQ.data as any)?.issuances || []}
                    locale={{ emptyText: 'No issuances linked to this case yet.' }}
                    columns={[
                      { title: 'Issuance', dataIndex: 'issuance_number', key: 'n' },
                      { title: 'Type', dataIndex: 'issuance_type', key: 't' },
                      {
                        title: 'Cost',
                        dataIndex: 'total_cost',
                        key: 'c',
                        render: (v: number | null) =>
                          v == null ? <Text type="secondary">Unavailable</Text> : Number(v).toFixed(2),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'labour',
              label: 'Labour',
              children: (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={jobQ.data?.labour || []}
                  locale={{ emptyText: 'No labour recorded. Hours only — labour cost is not fabricated.' }}
                  columns={[
                    { title: 'Role', dataIndex: 'role_code', key: 'r' },
                    { title: 'Employee', dataIndex: 'employee_id', key: 'e', render: (v: string) => v?.slice(0, 8) },
                    {
                      title: 'Started',
                      dataIndex: 'started_at',
                      render: (v: string) => (v ? dayjs(v).format('DD MMM HH:mm') : '—'),
                    },
                    {
                      title: 'Hours',
                      dataIndex: 'actual_hours',
                      render: (v: number | null) => (v == null ? '—' : Number(v).toFixed(2)),
                    },
                    { title: 'Status', dataIndex: 'line_status', key: 's' },
                    {
                      title: '',
                      key: 'x',
                      render: (_, row) =>
                        row.line_status === 'OPEN' && canLabour ? (
                          <Button
                            size="small"
                            onClick={() =>
                              run(() => workshopService.closeLabour(row.id), 'Labour stopped')
                            }
                          >
                            Stop
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              ),
            },
            {
              key: 'qc',
              label: 'QC / Release',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Alert
                    type="warning"
                    showIcon
                    message="Work complete is not the same as released"
                    description="Equipment returns to service only after QC (or valid waiver) and an authorized release. Workshop never freely sets PMV status."
                  />
                  <Descriptions size="small" column={1}>
                    <Descriptions.Item label="QC state">{ex ? qcLabel(ex.qc_state) : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Release">
                      {ex?.release_authorized_at
                        ? `Authorized ${dayjs(ex.release_authorized_at).format('DD MMM YYYY HH:mm')} → ${ex.release_target_status}`
                        : 'Not released'}
                    </Descriptions.Item>
                  </Descriptions>
                </Space>
              ),
            },
            {
              key: 'timeline',
              label: 'Timeline',
              children: (
                <Timeline
                  items={(jobQ.data?.stage_history || []).map((h) => ({
                    children: (
                      <span>
                        {stageLabel(h.from_stage)} → <strong>{stageLabel(h.to_stage)}</strong>
                        {h.to_hold ? ` · Hold: ${holdLabel(h.to_hold)}` : ''}
                        <br />
                        <Text type="secondary">{dayjs(h.changed_at).format('DD MMM YYYY HH:mm')}</Text>
                      </span>
                    ),
                  }))}
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Workshop intake"
        open={intakeOpen}
        onCancel={() => setIntakeOpen(false)}
        onOk={() => intakeForm.submit()}
        confirmLoading={false}
        destroyOnClose
      >
        <Form
          form={intakeForm}
          layout="vertical"
          onFinish={(values) => {
            if (!ex) return;
            const meter =
              values.meter_value != null && pmvId
                ? {
                    pmv_profile_id: pmvId,
                    reading_value: Number(values.meter_value),
                    meter_type: values.meter_type || 'HOUR_METER',
                    meter_unit: values.meter_unit || 'HOURS',
                  }
                : undefined;
            void run(
              () =>
                workshopService.recordIntake(ex.id, {
                  intake_notes: values.intake_notes,
                  intake_condition_notes: values.intake_condition_notes,
                  diagnosis_notes: values.diagnosis_notes,
                  meter,
                }),
              'Intake recorded'
            ).then(() => setIntakeOpen(false));
          }}
        >
          <Form.Item name="intake_notes" label="Complaint / operator report" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="intake_condition_notes" label="Visible condition">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="diagnosis_notes" label="Initial diagnosis">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="meter_value" label="Meter reading (uses PMV meter authority)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Apply hold"
        open={holdOpen}
        onCancel={() => setHoldOpen(false)}
        onOk={() => holdForm.submit()}
        destroyOnClose
      >
        <Form
          form={holdForm}
          layout="vertical"
          onFinish={(values) => {
            if (!ex) return;
            void run(
              () => workshopService.applyHold(ex.id, { hold_reason: values.hold_reason, reason: values.reason }),
              'Hold applied'
            ).then(() => setHoldOpen(false));
          }}
        >
          <Form.Item name="hold_reason" label="Hold reason" rules={[{ required: true }]}>
            <Select
              options={Object.entries(HOLD_LABELS).map(([value, label]) => ({ value, label }))}
            />
          </Form.Item>
          <Form.Item name="reason" label="Notes">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Start labour"
        open={labourOpen}
        onCancel={() => setLabourOpen(false)}
        onOk={() => labourForm.submit()}
        destroyOnClose
      >
        <Form
          form={labourForm}
          layout="vertical"
          initialValues={{ role_code: 'TECH' }}
          onFinish={(values) => {
            if (!ex) return;
            void run(
              () =>
                workshopService.startLabour(ex.id, {
                  employee_id: values.employee_id,
                  role_code: values.role_code,
                  work_performed: values.work_performed,
                }),
              'Labour started'
            ).then(() => setLabourOpen(false));
          }}
        >
          <Form.Item name="employee_id" label="Technician" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={(Array.isArray(employeesQ.data) ? employeesQ.data : []).map((e: any) => ({
                value: e.id,
                label:
                  [e.first_name, e.last_name].filter(Boolean).join(' ') ||
                  e.full_name ||
                  e.employee_code ||
                  e.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="role_code" label="Role">
            <Select
              options={[
                { value: 'LEAD', label: 'Lead' },
                { value: 'TECH', label: 'Technician' },
                { value: 'HELPER', label: 'Helper' },
              ]}
            />
          </Form.Item>
          <Form.Item name="work_performed" label="Work performed">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Authorize release"
        open={releaseOpen}
        onCancel={() => setReleaseOpen(false)}
        onOk={() => releaseForm.submit()}
        okText="Release equipment"
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This completes the maintenance case and requests a domain-controlled PMV status transition."
        />
        <Form
          form={releaseForm}
          layout="vertical"
          initialValues={{ release_target_status: 'AVAILABLE' }}
          onFinish={(values) => {
            if (!ex) return;
            void run(
              () =>
                workshopService.authorizeRelease(ex.id, {
                  release_target_status: values.release_target_status,
                  notes: values.notes,
                  expected_version: ex.version,
                }),
              'Equipment released'
            ).then(() => setReleaseOpen(false));
          }}
        >
          <Form.Item name="release_target_status" label="Return to service outcome" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'AVAILABLE', label: 'Available for operations' },
                { value: 'OUT_OF_SERVICE', label: 'Remain out of service' },
              ]}
            />
          </Form.Item>
          <Form.Item name="notes" label="Release notes">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Link existing material request"
        open={linkMrOpen}
        onCancel={() => setLinkMrOpen(false)}
        okText="Link to this job"
        onOk={() => {
          const id = linkMrId.trim();
          if (!id) {
            message.error('Enter a material request number or id');
            return;
          }
          void workshopService
            .linkPartsRequest(caseId, id)
            .then(() => {
              message.success('Material request linked');
              setLinkMrOpen(false);
              invalidate();
            })
            .catch((e: any) => {
              message.error(e?.response?.data?.message || e?.message || 'Failed to link request');
            });
        }}
        destroyOnClose
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Use this when parts were already requested/issued in Inventory without a maintenance case
          link. Enter the request number (e.g. MR-26-00008) or UUID.
        </Paragraph>
        <Input
          placeholder="MR-26-00008 or request UUID"
          value={linkMrId}
          onChange={(e) => setLinkMrId(e.target.value)}
        />
      </Modal>
    </WorkspaceLayout>
  );
};

export default WorkshopJobWorkspacePage;
